import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUserMemberships } from "@/hooks/company-membership/use-company-membership";
import { useCompanyStore } from "@/store/use-company-store";
import { useAuthStore } from "@/store/use-auth-store";
import { Button } from "@/components/ui/button";
import { Icon } from "@iconify/react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CompanyDetailsForm from "@/components/company/company-details-form";
import TeamMembersTable from "@/components/company/team-members-table";
import InvitationsTable from "@/components/company/invitations-table";
import { useQuery } from "@tanstack/react-query";
import { companyOnboardingService } from "@/services/company-onboarding/company-onboarding.service";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

const CompanyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { data: memberships, isLoading: membershipsLoading } = useUserMemberships({ 
    enabled: isAuthenticated 
  });
  const { currentCompanyId } = useCompanyStore();

  // Check onboarding status for active company - must be called before any conditional returns
  const { data: onboardingStatus } = useQuery({
    queryKey: ["companyOnboardingStatus", currentCompanyId],
    queryFn: () => companyOnboardingService.getStatus(currentCompanyId!),
    enabled: !!currentCompanyId,
    retry: false,
  });

  const handleCreateCompany = () => {
    navigate("/create-company");
  };

  // Find the active company membership
  const activeMembership = memberships?.find((m) => m.companyId === currentCompanyId);
  const company = activeMembership?.company;

  // Conditional returns after all hooks are declared
  if (membershipsLoading) {
    return (
      <div className="py-8 px-4">
        <Skeleton className="h-10 w-32 mb-6" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  // If no companies, show empty state
  if (!memberships || memberships.length === 0) {
    return (
      <div className="py-8 px-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Icon icon="lucide:building-2" className="h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {t("company.empty.title") || "No Companies Yet"}
            </h3>
            <p className="text-gray-600 mb-6 text-center max-w-md">
              {t("company.empty.description") ||
                "You're not a member of any companies yet. Create your first company to get started."}
            </p>
            <Button onClick={handleCreateCompany} className="gap-2">
              <Plus className="h-4 w-4" />
              {t("company.create.title") || "Create Company"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If no active company, show message to select one
  if (!activeMembership || !company) {
    return (
      <div className="py-8 px-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Icon icon="lucide:building-2" className="h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {t("company.noActiveCompany.title") || "No Active Company"}
            </h3>
            <p className="text-gray-600 mb-6 text-center max-w-md">
              {t("company.noActiveCompany.description") ||
                "Please select a company from the header switcher to view its details."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if onboarding is incomplete
  const isOnboardingIncomplete = onboardingStatus && !onboardingStatus.isComplete;

  return (
    <div className="py-8 px-4">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold">{company.name}</h1>
              <Badge variant="default">{t("company.myCompanies.active")}</Badge>
              <Badge variant="outline" className="capitalize">
                {t(`company.roles.${activeMembership.role.toLowerCase()}`) || activeMembership.role}
              </Badge>
            </div>
            {company.address && (
              <p className="text-gray-600 flex items-center gap-2">
                <Icon icon="lucide:map-pin" className="h-4 w-4" />
                {company.address}
              </p>
            )}
          </div>
          <Button onClick={handleCreateCompany} className="gap-2">
            <Plus className="h-4 w-4" />
            {t("company.create.title") || "Create Company"}
          </Button>
        </div>
      </div>

      {/* Show banner if onboarding is incomplete */}
      {isOnboardingIncomplete && (
        <Alert className="mb-6 border-yellow-200 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-800">
            {t("company.onboarding.incomplete") || "Company setup incomplete"}
          </AlertTitle>
          <AlertDescription className="text-yellow-700 mt-2">
            <p className="mb-3">
              {t("company.onboarding.incompleteBanner") || "Your company setup is not complete. Please complete the onboarding to access all features."}
            </p>
            <Button
              onClick={() => navigate(`/create-company?companyId=${currentCompanyId}`)}
              variant="outline"
              size="sm"
              className="border-yellow-300 text-yellow-800 hover:bg-yellow-100"
            >
              {t("company.onboarding.completeOnboarding") || "Complete Setup"}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-8">
          <TabsTrigger value="details">{t("company.tabs.details")}</TabsTrigger>
          <TabsTrigger value="team">{t("company.tabs.team")}</TabsTrigger>
          <TabsTrigger value="invitations">{t("company.tabs.invitations")}</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>{t("company.details.title")}</CardTitle>
              <CardDescription>
                {t("company.details.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CompanyDetailsForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team">
          <Card>
            <CardHeader>
              <CardTitle>{t("company.team.title")}</CardTitle>
              <CardDescription>
                {t("company.team.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TeamMembersTable />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invitations">
          <Card>
            <CardHeader>
              <CardTitle>{t("company.invitations.title")}</CardTitle>
              <CardDescription>
                {t("company.invitations.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <InvitationsTable />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompanyPage;
